import { useCallback, useState } from "react";
import { Formik, Field, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import classNames from "classnames";
import { useFeatures } from "flagged";
import { GLOBAL_INFRASTRUCTURE_REGIONS } from "../drawers.constants";
import { actions } from "../../../organisms/Drawer/drawers.reducers";
import Drawer, { selectSelectedDrawer } from "../../../organisms/Drawer";
import {
  createDataModel,
  resetDataModelDetails,
  setDataModelResources,
  setDataModelWithPendingResources,
} from "../../../../pages/DataModels";
import {
  BUTTON_TEXT,
  REGEX,
  STATUS,
  EMPTY_STRING,
  CLOUD_SERVICES,
  VALIDATION_MESSAGES,
} from "../../../../constants";
import Spinner, { SPINNER_NAMES } from "../../Spinner";
import { selectSubscription } from "../../../../pages/Users";
import { MODALS } from "../../AppModals/modals.constants";
import { actions as modalActions } from "../../Modal/modal.reducers";
import { actions as dataModalActions } from "../../../../pages/DataModels/dataModels.reducers";

const { S3_BUCKET_VALIDATION } = REGEX;
const { setSelectedDrawer } = actions;
const { setSelectedModal } = modalActions;
const { setAreResourcesPending, setSelectedDataModelId } = dataModalActions;

const { SUBMIT } = BUTTON_TEXT;
const { ACTIVE } = STATUS;
const { CREATE_DATA_MODEL } = SPINNER_NAMES;
const { COMING_SOON_MODAL } = MODALS;
const PAID = "paid";
const {
  DATA_MODAL_NAME_MIN_LENGTH,
  MAX_CHAR_LENGTH,
  NAME_SHOULD_CONTAIN,
  DATA_MODAL_NAME,
  SELECT_A_REGION,
  SELECT_CLOUD_SERVICE,
} = VALIDATION_MESSAGES;
const PENDING = "pending";

const ManageDataModelDrawer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { showAppDataModal } = useFeatures();

  const selectedDrawer = useSelector(selectSelectedDrawer);
  const selectedSubscription = useSelector(selectSubscription);

  const [isSpinnerActive, setIsSpinnerActive] = useState(false);
  const initialValues = {
    name: EMPTY_STRING,
    region: EMPTY_STRING,
    isEnabledRealTime: false,
    isEnabledRLS: false,
    status: ACTIVE,
    cloudService: EMPTY_STRING,
    template: EMPTY_STRING,
    datamodel: EMPTY_STRING,
  };

  const handleClose = useCallback(
    (resetForm) => {
      dispatch(setSelectedDrawer(null));
      resetForm();
    },
    [dispatch]
  );

  const validationSchema = Yup.object({
    name: Yup.string()
      .min(3, DATA_MODAL_NAME_MIN_LENGTH)
      .max(20, MAX_CHAR_LENGTH)
      .matches(S3_BUCKET_VALIDATION, NAME_SHOULD_CONTAIN)
      .required(DATA_MODAL_NAME),
    region: Yup.string().required(SELECT_A_REGION),
    cloudService: Yup.string().required(SELECT_CLOUD_SERVICE),
  });

  const handleOnSubmit = useCallback(
    (values, { resetForm }) => {
      if (!showAppDataModal) dispatch(setSelectedModal(COMING_SOON_MODAL));
      else {
        dispatch(resetDataModelDetails());
        dispatch(createDataModel(values)).then((res) => {
          if (!res.payload) return;
          const { collections, endpoints, services, awsResources } =
            res.payload.dataModelResources;
          dispatch(setSelectedDataModelId(res.payload.id));
          dispatch(setDataModelWithPendingResources(res.payload));
          dispatch(setDataModelResources(res.payload.dataModelResources));
          dispatch(
            setAreResourcesPending(
              collections === PENDING ||
                endpoints === PENDING ||
                services === PENDING ||
                awsResources === PENDING
            )
          );
          dispatch(setSelectedDrawer(null));
          resetForm();
          navigate(`/data-models/${res.payload.id}/collections`);
        });
      }
    },
    [dispatch, showAppDataModal, navigate]
  );

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleOnSubmit}
      validateOnMount={true}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        resetForm,
        isValid,
      }) => {
        const footer = (
          <div className="p-6 border-t border-gray-700 flex align-center justify-end">
            <button
              className="btn btn-primary-light mr-1"
              type="button"
              onClick={() => handleClose(resetForm)}
              disabled={isSpinnerActive}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              type={SUBMIT}
              disabled={isSpinnerActive || !isValid}
            >
              <Spinner
                name={CREATE_DATA_MODEL}
                setIsSpinnerActive={setIsSpinnerActive}
                persistSize
              >
                {SUBMIT}
              </Spinner>
            </button>
          </div>
        );

        return (
          <Form onSubmit={handleSubmit} autoComplete="off">
            <Drawer
              position="right"
              heading="Create Database"
              subHeading="Configure your data model settings."
              handleClose={() => handleClose(resetForm)}
              selectedDrawer={selectedDrawer}
              footer={footer}
            >
              <div className="scrollbar flex-1 overflow-auto">
                <div className="space-y-4 p-6 border-b border-gray-700">
                  <div className="form-group">
                    <label className="text-sm font-medium text-gray-50 mb-1 block">
                      Database Name
                      <span className="text-red-600 text-m mt-2">*</span>
                    </label>
                    <Field
                      name="name"
                      type="text"
                      className={classNames("form-control border-2", {
                        "border-red-600": touched.name && errors.name,
                      })}
                      placeholder="Database Name"
                      value={values.name}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      autoFocus
                    />
                    {touched.name && errors.name && (
                      <div className="text-sm font-medium text-red-500">
                        {errors.name}
                      </div>
                    )}
                    {!errors.name && selectedSubscription?.name === PAID && (
                      <div className="text-sm font-medium text-blue-500 mt-1">
                        {`AWS/GCP allowed name is : `}
                        <span className="text-sm font-medium text-green-500 b-9">
                          {values.name.replace(/\s+/g, "").toLocaleLowerCase()}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="text-sm font-medium text-gray-50 mb-1 block">
                      Region <span className="text-red-600 text-m mt-2">*</span>
                    </label>
                    <Field
                      as="select"
                      name="region"
                      className={classNames("form-control border-2", {
                        "border-red-600": touched.region && errors.region,
                      })}
                    >
                      <>
                        <option value={EMPTY_STRING}>Region</option>
                        {GLOBAL_INFRASTRUCTURE_REGIONS.map(
                          ({ value, name }) => (
                            <option key={value} value={value}>
                              {name}
                            </option>
                          )
                        )}
                      </>
                    </Field>
                    {touched.region && errors.region && (
                      <div className="text-sm font-medium text-red-500">
                        {errors.region}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="text-sm font-medium text-gray-50 mb-1 block">
                      Cloud Service
                      <span className="text-red-600 text-m mt-2">*</span>
                    </label>
                    <Field
                      as="select"
                      name="cloudService"
                      className={classNames("form-control border-2", {
                        "border-red-600":
                          touched.cloudService && errors.cloudService,
                      })}
                    >
                      <option value={EMPTY_STRING} hidden>
                        Select Service
                      </option>
                      {CLOUD_SERVICES.map((each) => (
                        <option value={each.value} key={each.name}>
                          {each.name}
                        </option>
                      ))}
                    </Field>
                    {touched.cloudService && errors.cloudService && (
                      <div className="text-sm font-medium text-red-500">
                        {errors.cloudService}
                      </div>
                    )}
                  </div>
                </div>
                {/* <div className="p-6">
                <h6 className="text-sm font-medium text-gray-50 mb-2">
                  Settings
                </h6>
                <div className="flex flex-row">
                  <label className="text-gray-300 flex mb-5">
                    <Field
                      type="checkbox"
                      name="isEnabledRealTime"
                      className="text-indigo-600 !rounded focus:ring-0 focus:outline-indigo-300 cursor-pointer"
                    />

                    <div className="ml-3 mt-0">
                      <div className="flex flex-row">
                        <h6 className="text-sm font-medium leading-4 mb-1">
                          Enable Realtime
                        </h6>
                        <Tooltip message={REALTIME} />
                      </div>
                      <span className="text-sm font-light leading-5 block">
                        Broadcast changes on this data collection to authorized
                        subscribers
                      </span>
                    </div>
                  </label>
                </div>

                <label className="text-gray-300 flex">
                  <Field
                    type="checkbox"
                    name="isEnabledRLS"
                    className="text-indigo-600 !rounded focus:ring-0 focus:outline-indigo-300 cursor-pointer"
                  />
                  <div className="ml-3">
                    <div className="flex flex-row">
                      <h6 className="text-sm font-medium leading-4 mb-1">
                        Enable Row Level Security
                      </h6>
                      <Tooltip message={RLS} />
                    </div>
                    <span className="text-sm font-light leading-5 block">
                      Restrict access to your data model by enabling RLS and
                      writing Postgres policies. If RLS is not enabled, anyone
                      with the annon key can modify and delete your data.
                    </span>
                  </div>
                </label>
              </div> */}
              </div>
            </Drawer>
          </Form>
        );
      }}
    </Formik>
  );
};

export default ManageDataModelDrawer;
